/**
 * The map instance, and the only place MapLibre internals are touched.
 *
 * Two rules make this file small and keep it that way.
 *
 * It owns the `Map`, the container and the style. Layers are added by hooks
 * (useDroneMarkers, useMissionLayer, useZoneLayer), each with its own sources,
 * so a change to mission rendering cannot reach the aircraft markers. The
 * predecessor's equivalent was one 5,107-line component with 94 props that drew
 * everything, and every layer change was a flight-display change.
 *
 * Layer setup is driven off the map's own load state through `ready()`, never
 * off React state. The predecessor tried the same imperative marker design and
 * reverted it, because it gated the fast path on a `mapLoaded` React flag that
 * never became true in its test harness: the imperative path silently never ran
 * and the React path shadowed it, and its own notes record the hit counter
 * sitting at zero on every check.
 */

import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

import { useBasemapConfig, type BasemapConfig } from '@/api/basemap';
import { BasemapToggle } from '@/map/BasemapToggle';
import { AORS, AOR_RADIUS_KM, lodForZoom } from '@/map/aors';
import { createLongPress } from '@/map/longPress';
import { useMapStore, type Basemap } from '@/stores/mapStore';

/**
 * The slice of MapLibre's Map that layer hooks are allowed to use.
 *
 * Narrow on purpose: a hook that can only add sources and layers cannot start
 * driving the camera from a telemetry callback, which in the predecessor meant
 * the map fought the operator's own panning at 5 Hz. Parameter and return types
 * are deliberately loose so a plain object stands in for a real map in tests.
 */
export interface MapLike {
  addSource(id: string, source: unknown): void;
  getSource(id: string): unknown;
  removeSource(id: string): void;
  addLayer(layer: unknown, before?: string): void;
  getLayer(id: string): unknown;
  removeLayer(id: string): void;
  /**
   * Show or hide a layer without removing it. Declared here because the LAYERS
   * switches drive it: rebuilding a layer on every toggle would re-fetch its
   * data and drop any accumulated state, and a hidden layer keeps receiving
   * updates so it is correct the instant it comes back.
   */
  setLayoutProperty(id: string, name: string, value: unknown): void;
  on(type: string, listener: (event: never) => void): unknown;
  off(type: string, listener: (event: never) => void): unknown;
  /**
   * The layer-scoped form. MapLibre only delivers `mouseenter`/`mouseleave`
   * per layer through it, so a layer that offers a hover affordance needs it;
   * declaring it here keeps that layer off the concrete map, and a fake map in
   * a test satisfies both signatures with one no-op method.
   */
  on(type: string, layerId: string, listener: (event: never) => void): unknown;
  off(type: string, layerId: string, listener: (event: never) => void): unknown;
}

/** What `getSource` returns for a GeoJSON source, once narrowed. */
export interface GeoJsonSourceLike {
  setData(data: unknown): void;
}

export function geoJsonSource(map: MapLike, id: string): GeoJsonSourceLike | null {
  const source = map.getSource(id) as GeoJsonSourceLike | undefined;
  return source && typeof source.setData === 'function' ? source : null;
}

export type MapTeardown = (() => void) | void;
export type MapSetup = (map: MapLike) => MapTeardown;

export interface MapClickPoint {
  lat: number;
  lon: number;
  shiftKey: boolean;
}

export interface MapContextPoint {
  lat: number;
  lon: number;
  /** Container pixel coordinates, for positioning the menu. */
  x: number;
  y: number;
}

export interface MapController {
  /**
   * Run `setup` once the style is usable, and again if the map is rebuilt.
   *
   * Registering after load runs it synchronously. There is no flag for a caller
   * to check and no state to get wrong: either the map is there, in which case
   * the work happens now, or it is not, in which case it happens on load.
   */
  ready: (setup: MapSetup) => () => void;
  /** The instance, or null before the container mounts. Do not gate layers on this. */
  instance: () => MapLike | null;
  /** The real MapLibre map, for markers and camera moves. Null before mount. */
  raw: () => maplibregl.Map | null;
  onClick: (handler: (point: MapClickPoint) => void) => () => void;
  /** Right-click on the map. The browser's own menu is suppressed. */
  onContextMenu: (handler: (point: MapContextPoint) => void) => () => void;
  flyTo: (lat: number, lon: number, zoom?: number) => void;
  fitPoints: (points: Array<[number, number]>, paddingPx?: number) => void;
}

export interface ControllerInternals extends MapController {
  attach: (map: maplibregl.Map) => void;
  detach: () => void;
}

/** One place both a right-click and a touch long-press open the map menu from. */
export function dispatchContext(
  handlers: Iterable<(point: MapContextPoint) => void>,
  point: MapContextPoint,
): void {
  for (const handler of handlers) handler(point);
}

export function createMapController(): ControllerInternals {
  let map: maplibregl.Map | null = null;
  let loaded = false;
  let styleListener: ((event: maplibregl.MapStyleDataEvent) => void) | null = null;
  const setups = new Set<MapSetup>();
  const teardowns = new Map<MapSetup, MapTeardown>();
  const clickHandlers = new Set<(point: MapClickPoint) => void>();
  const contextHandlers = new Set<(point: MapContextPoint) => void>();

  function runSetup(setup: MapSetup): void {
    if (!map || !loaded || teardowns.has(setup)) return;
    teardowns.set(setup, setup(map as unknown as MapLike));
  }

  function runTeardown(setup: MapSetup): void {
    const teardown = teardowns.get(setup);
    teardowns.delete(setup);
    if (typeof teardown === 'function') teardown();
  }

  return {
    attach: (instance) => {
      map = instance;
      loaded = false;

      const start = () => {
        if (loaded) return;
        loaded = true;
        for (const setup of setups) runSetup(setup);
      };

      // `styledata` with dataType "style" means the style specification is
      // parsed and sources may be added. It is not `isStyleLoaded()`, which
      // additionally waits for every source to finish loading tiles, and it is
      // not `load`, which waits for a rendered frame. Measured in a background
      // tab: the style parsed, twenty tiles were fetched, and both of the other
      // two signals stayed false indefinitely because a zero-sized container
      // never renders. A map with imagery and no aircraft on it is the exact
      // failure this rewrite exists to avoid.
      const onStyleData = (event: maplibregl.MapStyleDataEvent) => {
        // Start the moment the style spec is parsed. This fires even in a
        // zero-size background tab where tiles never load and isStyleLoaded()
        // and 'load' both stay false forever.
        if (event.dataType === 'style') {
          start();
          return;
        }
        // And on any later styledata once the style is fully loaded. The second
        // map created in a session — Planning, after Command — loads its style
        // from a warm cache, and its single 'style' event can fire before this
        // listener is attached. Without this branch that event is missed, the
        // attach-time isStyleLoaded() check is false because tiles are still
        // pending, and `loaded` never flips: base imagery renders but no overlay
        // — markers, tracks, zones — ever mounts. That is the "aircraft on
        // Command, none on Planning" bug. Any subsequent styledata (a tile
        // finishing) re-checks and unwedges it.
        if (instance.isStyleLoaded()) start();
      };
      styleListener = onStyleData;

      const press = createLongPress({
        onLongPress: ({ x, y }) => {
          const at = instance.unproject([x, y]);
          dispatchContext(contextHandlers, { lat: at.lat, lon: at.lng, x, y });
        },
      });
      const touchCount = (event: maplibregl.MapTouchEvent) => event.originalEvent.touches.length;
      instance.on('touchstart', (event) => press.start(event.point, touchCount(event)));
      instance.on('touchmove', (event) => press.move(event.point, touchCount(event)));
      instance.on('touchend', () => press.end());
      instance.on('touchcancel', () => press.end());
      // Panning is a press that moved; MapLibre may own the gesture before the
      // touchmove tolerance trips, so a pan start cancels it as well.
      instance.on('dragstart', () => press.end());

      instance.on('click', (event) => {
        // The lift that ends a long press can arrive as a click, which would
        // close the menu it just opened or, with GOTO armed, drop a target.
        if (press.firedRecently()) return;
        const point: MapClickPoint = {
          lat: event.lngLat.lat,
          lon: event.lngLat.lng,
          shiftKey: Boolean(event.originalEvent.shiftKey),
        };
        for (const handler of clickHandlers) handler(point);
      });

      instance.on('contextmenu', (event) => {
        // Suppress the browser menu; a right-click on the map is a command
        // affordance here, not a page action.
        event.preventDefault();
        // Android fires this for the same long press the touch path already
        // turned into a menu.
        if (press.firedRecently()) return;
        dispatchContext(contextHandlers, {
          lat: event.lngLat.lat,
          lon: event.lngLat.lng,
          x: event.point.x,
          y: event.point.y,
        });
      });

      instance.on('styledata', onStyleData);
      // 'load' fires once on a visible map after the first frame — a third
      // signal, so a missed styledata cannot wedge the normal case either.
      instance.on('load', start);
      // A style out of the browser cache can already be loaded here, and its
      // event has then been and gone.
      if (instance.isStyleLoaded()) start();
    },

    detach: () => {
      for (const setup of [...teardowns.keys()]) runTeardown(setup);
      if (map && styleListener) map.off('styledata', styleListener);
      styleListener = null;
      loaded = false;
      map = null;
    },

    ready: (setup) => {
      setups.add(setup);
      runSetup(setup);
      return () => {
        setups.delete(setup);
        runTeardown(setup);
      };
    },

    instance: () => (map && loaded ? (map as unknown as MapLike) : null),
    raw: () => map,

    onClick: (handler) => {
      clickHandlers.add(handler);
      return () => {
        clickHandlers.delete(handler);
      };
    },

    onContextMenu: (handler) => {
      contextHandlers.add(handler);
      return () => {
        contextHandlers.delete(handler);
      };
    },

    flyTo: (lat, lon, zoom) => {
      map?.flyTo({ center: [lon, lat], zoom: zoom ?? map.getZoom(), duration: 700 });
    },

    fitPoints: (points, paddingPx = 80) => {
      if (!map || points.length === 0) return;
      const first = points[0];
      if (!first) return;
      const bounds = new maplibregl.LngLatBounds(first, first);
      for (const point of points) bounds.extend(point);
      map.fitBounds(bounds, { padding: paddingPx, maxZoom: 18, duration: 600 });
    },
  };
}

/** The minimum of a MapLibre Marker that the drone layer drives. */
export interface MapMarker {
  setLngLat(lngLat: [number, number]): void;
  remove(): void;
}

/**
 * Attach a marker element to the map at a position.
 *
 * The position is not optional, and it is set before `addTo`. `addTo` appends
 * the element and then runs an update that dereferences the marker's position,
 * so a marker added without one throws inside MapLibre. Measured: the element
 * was in the DOM with no transform on it, the exception unwound the loop that
 * was creating markers, and every aircraft after the first was silently absent.
 *
 * This is also the only place a `MapLike` is turned back into a concrete map,
 * and the reason useDroneMarkers.ts imports nothing from MapLibre: this file
 * owns the library, every other map module owns a layer.
 */
export function createMapMarker(
  map: MapLike,
  element: HTMLElement,
  lngLat: [number, number],
): MapMarker {
  return new maplibregl.Marker({ element, anchor: 'center' })
    .setLngLat(lngLat)
    .addTo(map as unknown as maplibregl.Map);
}

/**
 * The slice of the map the basemap switch touches. The real MapLibre map
 * satisfies it; a plain object stands in for it in tests, the same way MapLike
 * lets the layer hooks run against a fake.
 */
export interface BasemapControls {
  getLayer(id: string): unknown;
  setLayoutProperty(id: string, name: string, value: unknown): void;
}

/**
 * Show exactly one of the two raster basemaps.
 *
 * Satellite hides the monochrome 'base' layer outright rather than recolouring
 * it, so the imagery renders in its own colour with no saturation paint fighting
 * it. Both layer ids are guarded because the style is swapped for the offline
 * build, where 'satellite' may be a different source and this must not throw.
 */
export function applyBasemap(map: BasemapControls, basemap: Basemap): void {
  const satelliteOn = basemap === 'satellite';
  if (map.getLayer('base')) {
    map.setLayoutProperty('base', 'visibility', satelliteOn ? 'none' : 'visible');
  }
  if (map.getLayer('satellite')) {
    map.setLayoutProperty('satellite', 'visibility', satelliteOn ? 'visible' : 'none');
  }
}

const MapContext = createContext<MapController | null>(null);

export function useMapController(): MapController {
  const controller = useContext(MapContext);
  if (!controller) {
    throw new Error('Map hooks must be used inside <MapCanvas>');
  }
  return controller;
}

/**
 * "Liquid Glass Monochrome" globe.
 *
 * A fully desaturated street raster over terrarium-encoded terrain and
 * hillshade, under a dark sky, with the AOR boundary dashed on top. Globe
 * projection makes it an orbital view when zoomed out and a Mercator map when
 * zoomed in — one continuous camera from orbit to tactical ground.
 *
 * The street tiles are whatever the server names (MapTiler with a key, OSM
 * without; see api/basemap.ts). For the offline / gov build they are swapped
 * for the self-hosted stack (PMTiles + mirrored terrarium + Sentinel-2); the
 * layer ids below stay the same so nothing downstream changes.
 */
const DEM_SOURCE = {
  type: 'raster-dem' as const,
  tiles: ['https://elevation-tiles-prod.s3.amazonaws.com/terrarium/{z}/{x}/{y}.png'],
  encoding: 'terrarium' as const,
  tileSize: 256,
  maxzoom: 13,
  attribution: 'Terrain: AWS / Mapzen Terrarium',
};

/**
 * The AOR boundaries, one dashed box per AOR, sized to the same radius that
 * scopes the fleet (AOR_RADIUS_KM). Derived from the AOR table so the square
 * an operator sees IS the boundary the data partitioning uses — the previous
 * hardcoded JAZIRA-only box matched neither and answered to nothing.
 */
function aorBoundaries(): unknown {
  return {
    type: 'FeatureCollection' as const,
    features: AORS.map((aor) => {
      const [lon, lat] = aor.center;
      const dLat = AOR_RADIUS_KM / 111;
      const dLon = AOR_RADIUS_KM / (111 * Math.cos((lat * Math.PI) / 180));
      return {
        type: 'Feature' as const,
        properties: { aorId: aor.id },
        geometry: {
          type: 'Polygon' as const,
          coordinates: [
            [
              [lon - dLon, lat - dLat],
              [lon + dLon, lat - dLat],
              [lon + dLon, lat + dLat],
              [lon - dLon, lat + dLat],
              [lon - dLon, lat - dLat],
            ],
          ],
        },
      };
    }),
  };
}

function mapStyle(basemap: BasemapConfig): maplibregl.StyleSpecification {
  return {
    version: 8 as const,
    projection: { type: 'globe' as const },
    sky: {
      'sky-color': '#111214',
      'horizon-color': '#2a2c30',
      'fog-color': '#0c0d0f',
      'sky-horizon-blend': 0.5,
      'horizon-fog-blend': 0.5,
      'fog-ground-blend': 0.6,
      'atmosphere-blend': ['interpolate', ['linear'], ['zoom'], 0, 1, 8, 0.35, 12, 0.1],
    },
    light: { anchor: 'viewport' as const, color: '#e8e8ea', intensity: 0.35, position: [1.3, 210, 30] },
    sources: {
      streets: {
        type: 'raster' as const,
        tiles: [basemap.tileUrl],
        tileSize: 256,
        maxzoom: basemap.maxzoom,
        attribution: basemap.attribution,
      },
      // ESRI World Imagery. Note the tile template is {z}/{y}/{x} — row before
      // column, the opposite of the streets {z}/{x}/{y} above; swapping them mirrors
      // the world. Off by default, shown only when the operator picks SAT.
      satellite: {
        type: 'raster' as const,
        tiles: [
          'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        ],
        tileSize: 256,
        maxzoom: 19,
        attribution: 'Imagery © Esri, Maxar, Earthstar Geographics',
      },
      terrainSrc: DEM_SOURCE,
      aor: {
        type: 'geojson' as const,
        data: aorBoundaries(),
      },
    },
    layers: [
      {
        id: 'base',
        type: 'raster' as const,
        source: 'streets',
        paint: {
          // Monochrome (saturation -1) and dark to match the glass aesthetic, but
          // brightness-max raised from the reference's 0.4: at tactical zoom over
          // sparse terrain 0.4 renders near-black, so features stay legible here.
          'raster-saturation': -1,
          'raster-brightness-max': 0.5,
          'raster-brightness-min': 0.03,
          'raster-contrast': 0.14,
        },
      },
      {
        // Above 'base' so it covers the monochrome raster, below the AOR overlay
        // so boundaries stay drawn on top of imagery. Hidden until SAT is picked;
        // no colour paint, so imagery renders in its own colour once 'base' hides.
        id: 'satellite',
        type: 'raster' as const,
        source: 'satellite',
        layout: { visibility: 'none' as const },
      },
      {
        id: 'aor-fill',
        type: 'fill' as const,
        source: 'aor',
        paint: {
          'fill-color': '#ffffff',
          'fill-opacity': ['interpolate', ['linear'], ['zoom'], 6, 0.07, 10, 0.015],
        },
      },
      {
        id: 'aor-line',
        type: 'line' as const,
        source: 'aor',
        paint: {
          'line-color': '#ffffff',
          'line-width': 1.4,
          'line-dasharray': [3, 2],
          'line-opacity': 0.75,
        },
      },
    ],
    // Terrain is NOT declared here: applying it inline blocks the first paint
    // while the DEM meshes, which froze the renderer for tens of seconds on
    // modest GPUs. useMapProfile applies it after the style has loaded, so the
    // globe paints immediately and terrain drapes in a beat later.
  } as unknown as maplibregl.StyleSpecification;
}

export interface MapCanvasProps {
  /** Opening view. Defaults to the orbital globe. */
  center?: { lat: number; lon: number };
  zoom?: number;
  pitch?: number;
  bearing?: number;
  /**
   * Clearance for MapLibre's own corner controls (scale bottom-left,
   * attribution bottom-right), in px. Each screen floats different glass
   * panels over the map's corners; without this the OSM attribution and the
   * scale bar end up underneath them.
   */
  controlInsets?: { bottom?: number; left?: number; right?: number };
  /** Overlays and layer hooks. Rendered inside the map context. */
  children?: ReactNode;
  className?: string;
}

// Screens that use the map (Planning, Flights) open on the tactical AOR, never
// the strategic globe — the globe is the Command entry, and CommandView passes
// that camera explicitly. Default to the primary AOR at tactical zoom.
const DEFAULT_CENTER = { lat: 36.29, lon: 43.63 };
const DEFAULT_ZOOM = 11;

/**
 * Apply the map-store render profile to a live map: 3D globe + terrain for
 * connected ops, or a flat 2D projection with no streamed terrain for edge /
 * air-gapped users. The layer hooks are profile-agnostic; only the base
 * projection and terrain change here.
 */
function useMapProfile(controller: MapController): void {
  const profile = useMapStore((state) => state.profile);
  const terrainOn = useMapStore((state) => state.terrainOn);
  const terrainEx = useMapStore((state) => state.terrainExaggeration);
  const aorOn = useMapStore((state) => state.layers.aor);

  useEffect(() => {
    const map = controller.raw();
    if (!map) return undefined;
    let cancelled = false;

    // Returns false if the style is not ready yet, so the caller retries on the
    // next idle. Applying to an unready style silently no-ops, which is what
    // made the 3D toggle fail to restore the globe after switching to 2D.
    const apply = (): boolean => {
      if (cancelled || !map.isStyleLoaded()) return false;
      // Terrain is heavy (DEM meshing) and only in 3D. Off by default so the
      // globe stays responsive; enabled from the LAYERS tab.
      if (profile === '3d' && terrainOn) {
        map.setTerrain({ source: 'terrainSrc', exaggeration: terrainEx });
      } else {
        map.setTerrain(null);
      }
      if (profile === '3d') {
        map.setProjection({ type: 'globe' });
      } else {
        map.setProjection({ type: 'mercator' });
        map.easeTo({ pitch: 0, duration: 300 });
      }
      for (const id of ['aor-fill', 'aor-line']) {
        if (map.getLayer(id)) {
          map.setLayoutProperty(id, 'visibility', aorOn ? 'visible' : 'none');
        }
      }
      return true;
    };

    if (apply()) return () => { cancelled = true; };

    const onIdle = () => {
      if (apply()) map.off('idle', onIdle);
    };
    map.on('idle', onIdle);
    return () => {
      cancelled = true;
      map.off('idle', onIdle);
    };
  }, [controller, profile, terrainOn, terrainEx, aorOn]);
}

/**
 * Toggle the two raster basemaps on the live map whenever the store's `basemap`
 * changes, and once on mount. Follows useMapProfile's idle-retry: applying to a
 * style that has not finished loading silently no-ops, so retry on the next idle.
 */
function useBasemap(controller: MapController): void {
  const basemap = useMapStore((state) => state.basemap);

  useEffect(() => {
    const map = controller.raw();
    if (!map) return undefined;
    let cancelled = false;

    const apply = (): boolean => {
      if (cancelled || !map.isStyleLoaded()) return false;
      applyBasemap(map, basemap);
      return true;
    };

    if (apply()) return () => { cancelled = true; };

    const onIdle = () => {
      if (apply()) map.off('idle', onIdle);
    };
    map.on('idle', onIdle);
    return () => {
      cancelled = true;
      map.off('idle', onIdle);
    };
  }, [controller, basemap]);
}

export function MapCanvas({
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  pitch = 0,
  bearing = 0,
  controlInsets,
  children,
  className,
}: MapCanvasProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [controller] = useState(createMapController);
  const [mapFailed, setMapFailed] = useState(false);
  // Held back until the server names the tile source, so a keyed deployment
  // never sends a single request to tile.openstreetmap.org first.
  const basemap = useBasemapConfig();

  useMapProfile(controller);
  useBasemap(controller);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !basemap) return undefined;

    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container,
        style: mapStyle(basemap),
        center: [center.lon, center.lat],
        zoom,
        maxZoom: 20,
        pitch,
        bearing,
        // Non-compact: the compact widget collapses to a toggle that hides the
        // tile credit entirely; the CSS above keeps the full credit small enough
        // to live in the corner, which both the license and the operator prefer.
        attributionControl: { compact: false },
      });
    } catch (error) {
      // WebGL context creation fails for real reasons in the field: a GPU
      // process wedged after sleep, hardware acceleration off, a locked-down
      // webview. The constructor THROWS, and uncaught it takes down the whole
      // React tree — a black screen on a C2 app. Degrade instead: no map, but
      // every panel (fleet, command, admin, planning data) stays usable.
      console.error('Map renderer unavailable:', error);
      setMapFailed(true);
      return undefined;
    }

    // Publish level-of-detail from zoom so the HUD can switch between the
    // strategic globe and the tactical COP.
    const publishLod = () => useMapStore.getState().setLod(lodForZoom(map.getZoom()));
    publishLod();
    map.on('zoom', publishLod);

    // Rotation is off. An operator reads bearings off this map and calls them
    // over the radio, and a map that has quietly been rotated by a two-finger
    // drag turns "target is north of you" into a wrong number.
    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();
    map.keyboard.disableRotation();
    // Double-click is waypoint placement in planning, not a zoom.
    map.doubleClickZoom.disable();

    // No +/- buttons. MapLibre pins them top-right, under the header on every
    // screen size, so they were never visible; zoom is the wheel, a pinch, or
    // the + and - keys.
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');

    // Persist the camera whenever it settles, so the two map screens are one
    // map: a tilted 3D view set up on Command carries into Planning instead of
    // snapping back to a flat top-down default.
    const saveCamera = () => {
      const center = map.getCenter();
      useMapStore.getState().setCamera({
        center: { lat: center.lat, lon: center.lng },
        zoom: map.getZoom(),
        pitch: map.getPitch(),
        bearing: map.getBearing(),
      });
    };
    map.on('moveend', saveCamera);

    controller.attach(map);

    // MapLibre measures the container once, in the constructor, and its
    // `trackResize` default watches the window rather than the element. So a
    // container that is still zero-height on mount, or that changes height
    // later, leaves the canvas at its original size forever: imagery renders
    // in a thin strip at the top and the rest of the pane stays black.
    //
    // Both cases are real here. The shell lays out after this effect runs, and
    // the activity log and the fleet rail both resize the map pane while it is
    // open. Observing the element covers all of it; a one-off resize on the
    // next frame would fix only the first.
    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => map.resize());
      observer.observe(container);
    }

    return () => {
      observer?.disconnect();
      controller.detach();
      map.remove();
    };
    // The camera props seed the first view only. Re-creating the map because a
    // default moved would throw away the operator's current pan and zoom.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [controller, basemap]);

  // 2px default: the compact controls are ~13px tall and the glass panels
  // float 14px off the edge, so the bottom gutter holds them fully visible.
  const insetVars = {
    '--ctrl-b': `${controlInsets?.bottom ?? 2}px`,
    '--ctrl-l': `${controlInsets?.left ?? 16}px`,
    '--ctrl-r': `${controlInsets?.right ?? 16}px`,
  } as CSSProperties;

  return (
    <div className={className ?? 'absolute inset-0'} style={insetVars}>
      {/* size-full is load-bearing: maplibre-gl's own CSS forces the container to
          position:relative, which overrides Tailwind's `absolute` and drops
          `inset-0` sizing, collapsing the map to zero height. An explicit
          percentage height sizes it whatever the resolved position is. */}
      <div ref={containerRef} className="absolute inset-0 size-full" data-testid="map-container" />
      {mapFailed ? (
        <div className="absolute inset-0 flex items-center justify-center" role="alert">
          <div className="glass max-w-[440px] rounded-[20px] p-6 text-center">
            <p className="label-mono text-[11px]" style={{ color: 'var(--caution)' }}>
              ⚠ MAP RENDERER UNAVAILABLE
            </p>
            <p className="mt-3 text-[12.5px] leading-relaxed text-[color:var(--muted-foreground)]">
              The browser could not start WebGL, which the map needs. This usually follows a
              sleep/wake cycle or disabled hardware acceleration — restart the browser to recover.
              Fleet, command, planning and admin panels remain available without the map.
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-4 rounded-[99px] bg-white/90 px-4 py-2 label-mono text-[10px] text-[#0c0c0d]"
            >
              RETRY
            </button>
          </div>
        </div>
      ) : null}
      {mapFailed ? null : <BasemapToggle />}
      <MapContext.Provider value={controller}>{children}</MapContext.Provider>
    </div>
  );
}

export default MapCanvas;
